import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';

import {
  setExpandedSection,
  setQid
} from '../../../redux/Slices/StartTestSlice/startFulTest';
import { useAppDispatch, useAppSelector } from '../../../redux/store/hooks';
import { useGetAllQuestionTypesQuery } from '../../../services/questionBankApi';
import { useGetAllCompanyQuery } from '../../../services/startTestAPI/startTestApi';
import { CleanFormattedText } from '../../../utils/HTMLtoPlainText';
import { renderHtmlWithTeX } from '../../../utils/RenderQuestionWithTex';
import AreasOfImprovement from '../../ReUsableComponents/CodingResults/AreasOfImprovement';
import PlagiarismCheck from '../../ReUsableComponents/CodingResults/PlagiarismCheck';
import TestCaseSummary from '../../ReUsableComponents/CodingResults/TestCaseSummary';

const QuestionContent = ({ isCloudPrepTest }: any) => {
  const { qId, resultById, testDetailsUnavailable } = useAppSelector(
    state => state.root.startFulltest
  );
  const { data: allQuestionTypesData } = useGetAllQuestionTypesQuery();
  const { data: companyData } = useGetAllCompanyQuery({});

  const getQuestionType = (questionTypeId: string) => {
    return (
      allQuestionTypesData?.data?.find(
        (questionType: any) => questionType.id === questionTypeId
      )?.questionTypeName ?? '-'
    );
  };

  const dispatch = useAppDispatch();
  const allSections = resultById?.testDTO?.examPatternDTO?.rounds[0]?.sections;

  const currentSection = allSections?.find(
    (section: any) => section.sectionId === qId?.sectionId
  );

  const currentSectionIndex = allSections?.findIndex(
    (section: any) => section.sectionId === qId?.sectionId
  );

  const currentQuestion =
    currentSection?.generatedSectionQuestions?.[qId?.questionIndex];
  const answer =
    qId?.sectionId && resultById?.answers
      ? resultById.answers[qId.sectionId]?.find(
          (a: any) => a.questionId === currentQuestion?.id
        )
      : null;
  const testCaseSummary =
    answer?.testCaseSummary?.totalTestCases > 0 ? answer.testCaseSummary : null;
  const testCaseResults = answer?.testCaseResults ?? [];
  const areasOfImprovement = answer?.areasOfImprovement ?? [];
  const isPlagiarismCheckPassed = answer?.isPlagiarismCheckPassed;

  const handleNavigate = (direction: 'next' | 'back') => {
    if (!currentSection) return;

    const allSections =
      resultById?.testDTO?.examPatternDTO?.rounds[0]?.sections;
    const currentSectionIndex = allSections?.findIndex(
      (section: any) => section.sectionId === qId.sectionId
    );

    let newIndex =
      direction === 'next' ? qId.questionIndex + 1 : qId.questionIndex - 1;
    let targetSection = currentSection;
    let targetSectionIndex = currentSectionIndex;

    // If we're at section boundary, try to move to next/previous section
    if (newIndex < 0 && currentSectionIndex > 0) {
      // Move to previous section's last question
      targetSectionIndex = currentSectionIndex - 1;
      targetSection = allSections[targetSectionIndex];
      newIndex = targetSection.generatedSectionQuestions.length - 1;
      dispatch(setExpandedSection(targetSection.sectionId));
    } else if (
      newIndex >= currentSection.generatedSectionQuestions.length &&
      currentSectionIndex < allSections.length - 1
    ) {
      // Move to next section's first question
      targetSectionIndex = currentSectionIndex + 1;
      targetSection = allSections[targetSectionIndex];
      newIndex = 0;
      dispatch(setExpandedSection(targetSection.sectionId));
    }

    // Only dispatch if we have a valid target
    if (
      newIndex >= 0 &&
      newIndex < targetSection.generatedSectionQuestions.length
    ) {
      const nextQuestion = targetSection.generatedSectionQuestions[newIndex];
      dispatch(
        setQid({
          questionId: nextQuestion.id,
          sectionId: targetSection.sectionId,
          questionIndex: newIndex
        })
      );
    }
  };
  const labels = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const getCompanyName = (testId?: string) => {
    const company = companyData?.data?.find(
      (company: any) => company.id === testId
    );
    return company?.companyName ? `${company?.companyName}` : ' -';
  };

  return (
    <>
      {currentQuestion ? (
        <>
          <div className='mb-4 flex items-start justify-between'>
            <div className='w-full'>
              <div className='flex w-full items-center justify-between'>
                <div>
                  <div className='mb-2 flex items-center gap-2'>
                    <p className='text-xl font-medium'>
                      Question {qId.questionIndex + 1}
                    </p>{' '}
                    <p>
                      {!answer?.userSelectedOption &&
                        !answer?.userSubmittedCode &&
                        '(Skipped)'}
                    </p>
                  </div>
                  {!isCloudPrepTest && (
                    <p className='text-xs font-semibold uppercase text-primary'>
                      Sub Topic: {currentQuestion?.subTopic}
                    </p>
                  )}
                  {answer?.languageUsed && (
                    <p className='mt-2 text-xs font-semibold uppercase text-primary'>
                      Language Used: {answer?.languageUsed}
                    </p>
                  )}
                  {!isCloudPrepTest && (
                    <p className='mt-2 text-xs font-semibold uppercase text-primary'>
                      Question Type:{' '}
                      {getQuestionType(currentQuestion?.questionTypeId)}
                    </p>
                  )}
                  <p className='mt-2 text-xs font-semibold uppercase text-[#0c604aa5]'>
                    Company :{' '}
                    {isCloudPrepTest
                      ? currentQuestion?.company ?? '-'
                      : getCompanyName(
                          resultById?.testDTO?.examPatternDTO?.companyId
                        )}
                  </p>
                  {isPlagiarismCheckPassed !== undefined &&
                    isPlagiarismCheckPassed !== null && (
                      <div className='mt-2'>
                        <PlagiarismCheck
                          isPlagiarismCheckPassed={isPlagiarismCheckPassed}
                        />
                      </div>
                    )}
                </div>
                <div className='flex items-center gap-x-6'>
                  <button
                    disabled={
                      qId.questionIndex === 0 && currentSectionIndex === 0
                    }
                    onClick={() => handleNavigate('back')}
                    className={`${
                      qId.questionIndex === 0 && currentSectionIndex === 0
                        ? 'pointer-events-none bg-[#E1E1E1]'
                        : 'border border-primary bg-primary hover:bg-white hover:text-primary'
                    } group group flex h-12 w-12 items-center justify-center rounded-xl`}
                  >
                    <MdKeyboardArrowLeft
                      className={` ${
                        qId.questionIndex === 0 && currentSectionIndex === 0
                          ? 'text-[#646464]'
                          : 'text-white'
                      } group-hover:text-primary`}
                      size={24}
                    />
                  </button>
                  <button
                    disabled={
                      qId.questionIndex ===
                        currentSection?.generatedSectionQuestions.length - 1 &&
                      currentSectionIndex === allSections?.length - 1
                    }
                    onClick={() => handleNavigate('next')}
                    className={`${
                      qId.questionIndex ===
                        currentSection?.generatedSectionQuestions.length - 1 &&
                      currentSectionIndex === allSections?.length - 1
                        ? 'pointer-events-none bg-[#E1E1E1]'
                        : 'border border-primary bg-primary hover:bg-white hover:text-primary'
                    } group group flex h-12 w-12 items-center justify-center rounded-xl`}
                  >
                    <MdKeyboardArrowRight
                      className={` ${
                        qId.questionIndex ===
                          currentSection?.generatedSectionQuestions.length -
                            1 && currentSectionIndex === allSections?.length - 1
                          ? 'text-[#646464]'
                          : 'text-white'
                      } group-hover:text-primary`}
                      size={24}
                    />
                  </button>
                </div>
              </div>
              <p className='mt-6 text-lg leading-[21.6px]'>
                {renderHtmlWithTeX(currentQuestion?.questionText, true)}
              </p>
              {answer?.userSubmittedCode && (
                <div className='mt-3'>
                  <span className='mb-2 font-medium'>Your Answer:</span>{' '}
                  <CleanFormattedText content={answer?.userSubmittedCode} />
                </div>
              )}
              {answer?.aiGeneratedAnswer && (
                <div className='mt-3'>
                  <span className='mb-2 font-medium'>Ideal Answer:</span>{' '}
                  <CleanFormattedText content={answer?.aiGeneratedAnswer} />
                </div>
              )}
              <div className='mt-6 grid w-full max-w-fit grid-cols-2 gap-4'>
                {currentQuestion?.options.map((option: any, index: any) => {
                  const isUserSelected =
                    option.answerOption === answer?.userSelectedOption;
                  const isActualCorrect =
                    option.answerOption === answer?.actualCorrectOption;
                  const correctAnswer = option.isCorrectAnswer;

                  let borderColor = 'border-gray-300'; // default

                  if (answer) {
                    if (answer.isCorrectAnswer && isUserSelected) {
                      borderColor = 'border-[#0B6E4F]';
                    } else if (!answer.isCorrectAnswer) {
                      if (isUserSelected) borderColor = 'border-[#D00000]';
                      if (isActualCorrect) borderColor = 'border-[#0B6E4F]';
                    }
                  } else {
                    // Not attempted
                    if (correctAnswer) {
                      borderColor = 'border-[#0B6E4F]';
                    }
                  }

                  return (
                    <div
                      key={option.answerOption}
                      className={`flex items-center justify-start rounded-xl border-[3px] p-3 ${borderColor} w-full rounded p-4`}
                    >
                      <span className='mr-2 font-semibold'>
                        {labels[index]}.
                      </span>
                      <span className='text-lg leading-[21.6px] text-gray-800'>
                        {renderHtmlWithTeX(option.answerOption, false)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {testCaseSummary && (
            <TestCaseSummary
              testCaseSummary={testCaseSummary}
              testCaseResults={testCaseResults}
            />
          )}
          {areasOfImprovement.length > 0 && (
            <AreasOfImprovement areasOfImprovement={areasOfImprovement} />
          )}
        </>
      ) : testDetailsUnavailable ? (
        <div>Question review is not available for this account.</div>
      ) : (
        <div>No question selected</div>
      )}
    </>
  );
};

export default QuestionContent;
